import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  Agent,
  AgentPreference,
  SessionAgentMapping,
  AgentCategory,
} from "@/types/agent";
import { mockAgents } from "@/constants/agents";
import { defaultAgentPreference } from "@/types/agent";

interface AgentState {
  currentAgent: Agent;
  setCurrentAgent: (agent: Agent) => void;
  resetToDefaultAgent: () => void;
  agentDismissed: boolean;
  setAgentDismissed: (dismissed: boolean) => void;

  addedAgents: string[];
  addAgent: (agentId: string) => void;
  removeAgent: (agentId: string) => void;
  isAgentAdded: (agentId: string) => boolean;

  agentPreferences: Record<string, AgentPreference>;
  setAgentPreference: (
    agentId: string,
    preference: Partial<AgentPreference>,
  ) => void;
  getAgentPreference: (agentId: string) => AgentPreference;

  sessionMappings: SessionAgentMapping[];
  setSessionAgent: (sessionId: string, agentId: string) => void;
  getSessionAgent: (sessionId: string) => Agent | undefined;

  getDefaultAgent: () => Agent;

  getAgentsByCategory: (category: AgentCategory | "all") => Agent[];
  getAddedAgentsList: () => Agent[];
}

export const useAgentStore = create<AgentState>()(
  persist(
    (set, get) => ({
      currentAgent: mockAgents[0],

      addedAgents: ["default"],

      addAgent: (agentId) => {
        set((state) => ({
          addedAgents: state.addedAgents.includes(agentId)
            ? state.addedAgents
            : [...state.addedAgents, agentId],
        }));
      },

      removeAgent: (agentId) => {
        set((state) => ({
          addedAgents: state.addedAgents.filter((id) => id !== agentId),
        }));
      },

      isAgentAdded: (agentId) => {
        return get().addedAgents.includes(agentId);
      },

      agentPreferences: {},

      sessionMappings: [],

      setCurrentAgent: (agent) => set({ currentAgent: agent }),

      resetToDefaultAgent: () => {
        const defaultAgent = get().getDefaultAgent();
        set({ currentAgent: defaultAgent, agentDismissed: false });
      },

      agentDismissed: false,
      setAgentDismissed: (dismissed) => set({ agentDismissed: dismissed }),

      setAgentPreference: (agentId, preference) => {
        set((state) => ({
          agentPreferences: {
            ...state.agentPreferences,
            [agentId]: {
              ...defaultAgentPreference,
              ...state.agentPreferences[agentId],
              ...preference,
            },
          },
        }));
      },

      getAgentPreference: (agentId) => {
        return get().agentPreferences[agentId] || defaultAgentPreference;
      },

      setSessionAgent: (sessionId, agentId) => {
        set((state) => ({
          sessionMappings: [
            ...state.sessionMappings.filter((m) => m.sessionId !== sessionId),
            { sessionId, agentId, createdAt: new Date() },
          ],
        }));
      },

      getSessionAgent: (sessionId) => {
        const mapping = get().sessionMappings.find(
          (m) => m.sessionId === sessionId,
        );
        if (!mapping) return undefined;
        return mockAgents.find((a) => a.id === mapping.agentId);
      },

      getDefaultAgent: () => {
        return mockAgents.find((a) => a.isDefault) || mockAgents[0];
      },

      getAgentsByCategory: (category) => {
        if (category === "all") return mockAgents;
        return mockAgents.filter((agent) => agent.category === category);
      },

      getAddedAgentsList: () => {
        const { addedAgents } = get();
        return mockAgents.filter((agent) => addedAgents.includes(agent.id));
      },
    }),
    {
      name: "agent-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentAgent: state.currentAgent,
        addedAgents: state.addedAgents,
        agentPreferences: state.agentPreferences,
        sessionMappings: state.sessionMappings,
      }),
    },
  ),
);
